import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Plus, Search, Pencil, Trash2, Loader2, UserRound } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", email: "", phone: "", department: "", designation: "", joining_date: "", dob: "",
};

function EmployeeForm({ value, onChange, onSubmit, submitting, submitLabel }) {
  const field = (id, label, type = "text", required = true) => (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      <Input
        id={id}
        type={type}
        data-testid={`input-${id}`}
        value={value[id] || ""}
        onChange={(e) => onChange({ ...value, [id]: e.target.value })}
        required={required}
        className="mt-1.5 h-10"
      />
    </div>
  );
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      className="space-y-4"
      data-testid="employee-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {field("name", "Full name")}
        {field("email", "Email", "email")}
        {field("phone", "Phone")}
        {field("department", "Department")}
        {field("designation", "Designation")}
        {field("joining_date", "Joining date", "date")}
        {field("dob", "Date of birth", "date", false)}
      </div>
      <DialogFooter className="pt-2">
        <Button type="submit" disabled={submitting} className="btn-lift" data-testid="employee-form-submit">
          {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  // Add dialog
  const [addOpen, setAddOpen] = useState(false);
  const [addData, setAddData] = useState(empty);
  const [adding, setAdding] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setEmployees(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addEmployee = async () => {
    setAdding(true);
    try {
      await api.post("/employees", addData);
      toast.success("Employee added");
      setAddOpen(false);
      setAddData(empty);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdding(false); }
  };

  const startEdit = (emp) => {
    setEditData({ ...emp });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setEditing(true);
    try {
      const { employee_id, name, email, phone, department, designation, joining_date, dob } = editData;
      await api.put(`/employees/${employee_id}`, { name, email, phone, department, designation, joining_date, dob: dob || null });
      toast.success("Employee updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEditing(false); }
  };

  const removeEmployee = async (id) => {
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Employee removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = employees.filter((e) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [e.name, e.email, e.department, e.designation].some((f) => f?.toLowerCase().includes(q));
  });

  return (
    <div className="p-8 lg:p-10" data-testid="employees-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mini text-[11px] uppercase tracking-[0.25em] text-muted-foreground">Roster</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Employees</h1>
          <p className="text-sm text-muted-foreground mt-2">
            {employees.length} {employees.length === 1 ? "person" : "people"} on the payroll
          </p>
        </div>

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-employee-btn" className="btn-lift bg-brand hover:opacity-90 text-white rounded-xl shadow-soft h-10 px-5">
              <Plus className="h-4 w-4 mr-2" /> Add employee
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" data-testid="add-employee-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">New employee</DialogTitle>
              <DialogDescription>Fill in the details below. Email must be unique.</DialogDescription>
            </DialogHeader>
            <EmployeeForm
              value={addData}
              onChange={setAddData}
              onSubmit={addEmployee}
              submitting={adding}
              submitLabel="Add employee"
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="employees-search"
            placeholder="Search by name, department…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
      </div>

      <div className="mt-4 border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60 hover:bg-secondary/60">
              <TableHead className="w-[35%]">Employee</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" /> Loading…
              </TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-14 text-center">
                  <UserRound className="h-6 w-6 mx-auto text-muted-foreground" />
                  <div className="mt-3 text-sm text-muted-foreground">
                    {employees.length === 0 ? "No employees yet. Add your first one." : "No matches for your search."}
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.map((emp) => {
              const initials = (emp.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
              return (
                <TableRow
                  key={emp.employee_id}
                  data-testid={`employee-row-${emp.employee_id}`}
                  className="hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
                >
                  <TableCell>
                    <Link
                      to={`/presently/employees/${emp.employee_id}`}
                      className="flex items-center gap-3 group"
                      data-testid={`employee-link-${emp.employee_id}`}
                    >
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-sm font-medium group-hover:underline underline-offset-4">{emp.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{emp.email}</div>
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{emp.department}</TableCell>
                  <TableCell className="text-sm">{emp.designation}</TableCell>
                  <TableCell className="text-sm text-muted-foreground tabular-nums font-mono-mini">{emp.joining_date}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => startEdit(emp)}
                        data-testid={`edit-employee-${emp.employee_id}`}
                        className="h-8 w-8"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`delete-employee-${emp.employee_id}`}
                            className="h-8 w-8 text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove {emp.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will delete their profile and all attendance history. This can't be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              data-testid={`confirm-delete-${emp.employee_id}`}
                              onClick={() => removeEmployee(emp.employee_id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit employee</DialogTitle>
            <DialogDescription>Update details and save.</DialogDescription>
          </DialogHeader>
          {editData && (
            <EmployeeForm
              value={editData}
              onChange={setEditData}
              onSubmit={saveEdit}
              submitting={editing}
              submitLabel="Save changes"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
